<!--
  TagFilterBar — G1.7 widget parity for admin's TagFilterBarComponent.

  Toolbar above the conversation list with:
    - Colored chips for active filters (× to remove individually).
    - "+ Filter by tag" / "+ Add" button opens a checkbox popover of all
      catalog tags (lazy-loaded via tagsList query on first open).
    - "Clear all" link when filter is non-empty.

  State (selectedTagIds) is owned by the parent (ConversationsPage) so
  it can persist + pass to conversationsList(tagIds). This component is
  dumb — value in, change out. Self-closes the popover on outside click
  + Escape.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { tagsList, type Tag } from '../lib/api/queries.js';

  type Props = {
    selectedTagIds: string[];
    onChange: (next: string[]) => void;
  };
  let { selectedTagIds, onChange }: Props = $props();

  let open = $state(false);
  const catalogQuery = $derived(createQuery(tagsList()));

  // Resolve selected IDs against the catalog so chips can render with
  // colors. Falls back to a neutral pill when the catalog hasn't loaded
  // yet (or the operator's filter references a deleted tag).
  const selectedTags = $derived<Tag[]>(
    selectedTagIds.map((id) => {
      const hit = $catalogQuery.data?.find((t) => t.id === id);
      return (
        hit ?? {
          id,
          tenantId: '',
          name: id.slice(0, 8),
          slug: '',
          color: '#888888',
          createdAt: '',
          updatedAt: '',
        }
      );
    }),
  );

  function isSelected(id: string): boolean {
    return selectedTagIds.includes(id);
  }

  function toggleOpen(event: Event) {
    event.stopPropagation();
    open = !open;
  }

  function remove(id: string) {
    onChange(selectedTagIds.filter((t) => t !== id));
  }
  function clearAll() {
    onChange([]);
  }
  function onToggle(id: string, checked: boolean) {
    if (checked && !selectedTagIds.includes(id)) {
      onChange([...selectedTagIds, id]);
    } else if (!checked && selectedTagIds.includes(id)) {
      onChange(selectedTagIds.filter((t) => t !== id));
    }
  }

  // ---- outside-click + Escape -----------------------------------------

  let hostEl: HTMLDivElement | null = $state(null);

  function handleDocClick(event: MouseEvent) {
    if (!open) return;
    const target = event.target as Node | null;
    if (target && hostEl?.contains(target)) return;
    open = false;
  }
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && open) open = false;
  }
  onMount(() => {
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKeydown);
  });
  onDestroy(() => {
    document.removeEventListener('click', handleDocClick);
    document.removeEventListener('keydown', handleKeydown);
  });
</script>

<div class="bar" role="region" aria-label="Filter conversations by tag" bind:this={hostEl}>
  {#if selectedTags.length > 0}
    <ul class="active" role="list" aria-label="Active tag filters">
      {#each selectedTags as t (t.id)}
        <li>
          <button
            type="button"
            class="active-chip"
            aria-label={`Remove filter ${t.name}`}
            style="background-color: color-mix(in oklch, {t.color} 22%, transparent); color: color-mix(in oklch, {t.color} 70%, black); border-color: color-mix(in oklch, {t.color} 40%, transparent);"
            onclick={() => remove(t.id)}
          >
            <span class="name">{t.name}</span>
            <span class="x" aria-hidden="true">×</span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}

  <div class="picker-anchor">
    <button
      type="button"
      class="add-btn"
      aria-haspopup="dialog"
      aria-expanded={open}
      onclick={toggleOpen}
    >
      {selectedTags.length === 0 ? '+ Filter by tag' : '+ Add'}
    </button>

    {#if open}
      <div class="popover" role="dialog" aria-label="Choose tags to filter by">
        {#if $catalogQuery.isPending}
          <div class="empty" role="status">Loading...</div>
        {:else if !$catalogQuery.data || $catalogQuery.data.length === 0}
          <div class="empty">No tags defined yet.</div>
        {:else}
          <ul class="catalog" role="list">
            {#each $catalogQuery.data as t (t.id)}
              <li>
                <label class="tag-row">
                  <input
                    type="checkbox"
                    checked={isSelected(t.id)}
                    onchange={(e) => onToggle(t.id, (e.currentTarget as HTMLInputElement).checked)}
                  />
                  <span class="swatch" style="background-color: {t.color}" aria-hidden="true"></span>
                  <span class="name">{t.name}</span>
                </label>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}
  </div>

  {#if selectedTags.length > 0}
    <button
      type="button"
      class="clear-link"
      aria-label="Clear all tag filters"
      onclick={clearAll}
    >Clear all</button>
  {/if}
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
  }
  .active {
    list-style: none;
    margin: 0;
    padding: 0;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .active-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px 2px 8px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: var(--widget-font);
    max-width: 180px;
  }
  .active-chip .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .active-chip .x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 14px;
    line-height: 1;
    opacity: 0.7;
  }
  .active-chip:hover .x {
    opacity: 1;
  }
  .active-chip:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }

  .picker-anchor {
    position: relative;
  }
  .add-btn {
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: 1px dashed var(--widget-border, oklch(92% 0.005 250));
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    cursor: pointer;
    font-family: var(--widget-font);
  }
  .add-btn:hover {
    border-style: solid;
    color: var(--widget-fg, oklch(20% 0.01 250));
    border-color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }
  .add-btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }

  .popover {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 240px;
    z-index: 30;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 8px;
    box-shadow:
      0 1px 2px oklch(20% 0.01 250 / 0.06),
      0 10px 28px oklch(20% 0.01 250 / 0.14);
  }
  .empty {
    padding: 14px 12px;
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 12px;
  }
  .catalog {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 220px;
    overflow-y: auto;
  }
  .tag-row {
    display: grid;
    grid-template-columns: auto auto 1fr;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    cursor: pointer;
    font-size: 12.5px;
  }
  .tag-row:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, transparent);
  }
  .tag-row input[type='checkbox'] {
    accent-color: var(--widget-accent, oklch(75% 0.165 75));
    cursor: pointer;
  }
  .swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 15%, transparent);
  }
  .tag-row .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .clear-link {
    background: none;
    border: none;
    padding: 0 4px;
    font-size: 11.5px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    cursor: pointer;
    text-decoration: underline;
    font-family: var(--widget-font);
  }
  .clear-link:hover {
    color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .clear-link:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
</style>
